import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

/**
 * Service for managing ReadyPlayerMe assets and user inventory
 * Handles unlocking, locking, and equipping items through RPM API
 */
export class ReadyPlayerMeAssetService {
  /**
   * Unlock an asset for a specific user
   * @param assetId - The ReadyPlayerMe asset ID
   * @param userId - The user's ID
   */
  static async unlockAsset(assetId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const unlockAsset = httpsCallable(functions, 'unlockRpmAsset');
      const result = await unlockAsset({ assetId, userId });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to unlock RPM asset:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to unlock asset' 
      };
    }
  }

  /**
   * Lock an asset for a specific user (revoke access)
   * @param assetId - The ReadyPlayerMe asset ID
   * @param userId - The user's ID
   */
  static async lockAsset(assetId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const lockAsset = httpsCallable(functions, 'lockRpmAsset');
      const result = await lockAsset({ assetId, userId });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to lock RPM asset:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to lock asset' 
      };
    }
  }

  /**
   * Equip an asset on a user's avatar
   * @param avatarId - The user's ReadyPlayerMe avatar ID
   * @param assetId - The asset to equip
   */
  static async equipAsset(avatarId: string, assetId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const equipAsset = httpsCallable(functions, 'equipRpmAsset');
      const result = await equipAsset({ avatarId, assetId });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to equip RPM asset:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to equip asset' 
      };
    }
  }

  /**
   * Unequip an asset from a user's avatar
   * @param avatarId - The user's ReadyPlayerMe avatar ID
   * @param assetId - The asset to unequip
   */
  static async unequipAsset(avatarId: string, assetId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const unequipAsset = httpsCallable(functions, 'unequipRpmAsset');
      const result = await unequipAsset({ avatarId, assetId });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to unequip RPM asset:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to unequip asset' 
      };
    }
  }

  /**
   * Get user's unlocked assets
   * @param userId - The user's ID
   */
  static async getUserUnlockedAssets(userId: string): Promise<{ success: boolean; assets?: any[]; error?: string }> {
    try {
      const getUserAssets = httpsCallable(functions, 'getUserRpmAssets');
      const result = await getUserAssets({ userId });
      
      return { 
        success: true, 
        assets: result.data as any[] 
      };
    } catch (error) {
      console.error('Failed to get user RPM assets:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get user assets' 
      };
    }
  }

  /**
   * Create a new asset in ReadyPlayerMe (for coaches)
   * @param assetData - Asset creation data
   */
  static async createAsset(assetData: {
    name: string;
    category: string;
    locked?: boolean;
    visibility?: 'public' | 'private';
  }): Promise<{ success: boolean; assetId?: string; error?: string }> {
    try {
      const createAsset = httpsCallable(functions, 'createRpmAsset');
      const result = await createAsset(assetData);
      
      return { 
        success: true, 
        assetId: result.data as string 
      };
    } catch (error) {
      console.error('Failed to create RPM asset:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create asset' 
      };
    }
  }
}
